import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  FileText,
  Film,
  FolderInput,
  FolderPlus,
  Image as ImageIcon,
  Layers,
  Music,
  RotateCcw,
  Search,
  Trash2,
  Unlink,
  Upload,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import {
  mediaFolderContents,
  mediaFolderCreate,
  mediaFolderDelete,
  mediaFolderRestore,
  mediaFolderUpdate,
  mediaFolders,
  mediaList,
  mediaMove,
  mediaIDs,
  mediaPurge,
  mediaRestoreMany,
  mediaTrashMany,
} from '@/shared/api/generated/media/media';
import type {
  AssetBody,
  FolderBody,
  MediaListParams,
  PurgeInputBody,
} from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { pluralize } from '@/shared/lib/plural';
import { useDebounced } from '@/shared/lib/useDebounced';
import { AuthImage } from '@/shared/ui/AuthImage';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { ConfirmDialog, Dialog, DialogContent } from '@/shared/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { Input } from '@/shared/ui/Input';
import { toast } from '@/shared/ui/Toast';

import { AssetDetails, NameDialog } from './AssetDetails';
import { FolderTree, MEDIA_DRAG } from './FolderTree';
import { humanSize } from './format';
import styles from './MediaLibrary.module.scss';
import { useUploader, type Environment } from './useUploader';

type Kind = 'image' | 'video' | 'audio' | 'document';

/** Где человек в медиатеке: в папке (null — корень) или в быстром виде. */
type Place =
  | { view: 'folder'; folder: string | null }
  | { view: 'kind'; kind: Kind }
  | { view: 'unused' }
  | { view: 'trash' };

const QUICK: { place: Place; label: string; icon: ReactNode }[] = [
  { place: { view: 'kind', kind: 'image' }, label: 'Картинки', icon: <ImageIcon size={15} /> },
  { place: { view: 'kind', kind: 'video' }, label: 'Видео', icon: <Film size={15} /> },
  { place: { view: 'kind', kind: 'audio' }, label: 'Аудио', icon: <Music size={15} /> },
  { place: { view: 'kind', kind: 'document' }, label: 'Документы', icon: <FileText size={15} /> },
  { place: { view: 'unused' }, label: 'Неиспользуемые', icon: <Unlink size={15} /> },
  { place: { view: 'trash' }, label: 'Корзина', icon: <Trash2 size={15} /> },
];

export interface MediaLibraryProps {
  siteId: string;
  env: Environment;
  canWrite: boolean;
  canRemove: boolean;
  /** Режим выбора файла для поля записи: какой вид годится и что выбрано. */
  pick?: {
    kind: 'image' | 'any';
    chosenID?: string | undefined;
    onChoose: (asset: AssetBody) => void;
    /** Несколько файлов: щелчок отмечает, «Добавить» отдаёт отмеченные. */
    onChooseMany?: (assets: AssetBody[]) => void;
  };
}

/**
 * Медиатека окружения (docs/research/09-media.md): папки любой вложенности
 * и быстрые виды слева, файлы папки посередине, сведения о файле справа.
 *
 * Одна и та же и на экране «Медиа», и в окне выбора файла для записи: там
 * она открывается в последней папке и сразу принимает загрузку туда же.
 */
export function MediaLibrary({ siteId, env, canWrite, canRemove, pick }: MediaLibraryProps) {
  const queryClient = useQueryClient();
  const memoryKey = `faceta.media.folder.${siteId}.${env}`;

  const [place, setPlace] = useState<Place>(() => {
    if (pick?.kind === 'image') {
      return { view: 'kind', kind: 'image' };
    }

    // Выбор файла открывается там, где человек был в прошлый раз: картинки к
    // статьям обычно лежат в одной папке.
    try {
      const last = localStorage.getItem(memoryKey);

      return { view: 'folder', folder: last === null || last === '' ? null : last };
    } catch {
      return { view: 'folder', folder: null };
    }
  });

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  // Опорный файл для выбора с Shift: от него до щёлкнутого выбирается всё.
  const anchor = useRef<string>(undefined);
  const [confirm, setConfirm] = useState<{
    title: string;
    description: string;
    run: () => Promise<void>;
  }>();
  const [picked, setPicked] = useState<AssetBody[]>([]);
  const many = pick?.onChooseMany !== undefined;
  const [detail, setDetail] = useState<string>();
  const [naming, setNaming] = useState<{
    title: string;
    initial: string;
    save: (name: string) => void;
  }>();
  const [removingFolder, setRemovingFolder] = useState<FolderBody>();
  const [posterFor, setPosterFor] = useState<(poster: AssetBody) => void>();
  const [dropping, setDropping] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounced(search);
  const currentFolder = place.view === 'folder' ? place.folder : undefined;

  useEffect(() => {
    if (place.view === 'folder') {
      try {
        localStorage.setItem(memoryKey, place.folder ?? '');
      } catch {
        // Без памяти о папке — не беда.
      }
    }
  }, [place, memoryKey]);

  const folders = useQuery({
    queryKey: ['media-folders', siteId, env],
    queryFn: async () => {
      const response = await mediaFolders(siteId, env);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос папок');
      }

      return response.data.items ?? [];
    },
  });

  const trashFolders = useQuery({
    queryKey: ['media-folders', siteId, env, 'trash'],
    enabled: place.view === 'trash',
    queryFn: async () => {
      const response = await mediaFolders(siteId, env, { deleted: true });

      return response.status === 200 ? (response.data.items ?? []) : [];
    },
  });

  // Папка, которой больше нет (удалили в другой вкладке), — назад в корень.
  const folderList = folders.data ?? [];
  const missing =
    place.view === 'folder' &&
    place.folder !== null &&
    folders.data !== undefined &&
    !folderList.some((item) => item.id === place.folder);

  if (missing) {
    setPlace({ view: 'folder', folder: null });
  }

  const params: MediaListParams = {
    page,
    per_page: 48,
    ...(debouncedSearch === '' ? {} : { search: debouncedSearch }),
    ...(place.view === 'folder' && debouncedSearch === ''
      ? { folder: place.folder ?? 'root' }
      : {}),
    ...(place.view === 'kind' ? { type: place.kind } : {}),
    ...(place.view === 'unused' ? { unused: true } : {}),
    ...(place.view === 'trash' ? { deleted: true } : {}),
    ...(pick?.kind === 'image' ? { type: 'image' as const } : {}),
  };

  const list = useQuery({
    queryKey: ['media', siteId, env, params],
    queryFn: async () => {
      const response = await mediaList(siteId, env, params);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос медиатеки');
      }

      return response.data;
    },
    placeholderData: (previous) => previous,
    // Видео перекодируется — список обновляется, пока не закончится: виден
    // ход, а готовое видео само становится готовым.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some(
        (item) => item.hls_status === 'queued' || item.hls_status === 'processing',
      )
        ? 3000
        : false,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['media', siteId, env] });
    void queryClient.invalidateQueries({ queryKey: ['media-folders', siteId, env] });
    void queryClient.invalidateQueries({ queryKey: ['media-usage'] });
  }, [queryClient, siteId, env]);

  const uploader = useUploader(siteId, env, refresh);

  const go = (next: Place) => {
    setPlace(next);
    setPage(1);
    setSelected([]);
    setSearch('');
  };

  const fail = (cause: unknown) => {
    toast.error('Не получилось', {
      description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
    });
  };

  // Удалить навсегда: окно подтверждения — вернуть будет нельзя.
  const askPurge = (body: PurgeInputBody, title: string, description: string) => {
    setConfirm({
      title,
      description,
      run: async () => {
        try {
          const response = await mediaPurge(siteId, env, body);

          if (response.status !== 200) {
            throw new Error('Неожиданный ответ на удаление');
          }

          const { files, folders, freed } = response.data;
          const parts = [
            files > 0 ? pluralize(files, ['файл', 'файла', 'файлов']) : '',
            folders > 0 ? pluralize(folders, ['папка', 'папки', 'папок']) : '',
          ].filter(Boolean);

          toast.success(`Удалено навсегда: ${parts.join(' и ') || 'ничего'}`, {
            ...(freed > 0 ? { description: `Освободилось ${humanSize(freed)} на диске.` } : {}),
          });
          setSelected([]);
          setDetail(undefined);
          refresh();
        } catch (cause) {
          fail(cause);

          throw cause;
        }
      },
    });
  };

  const move = (folder: string | null, ids: string[]) => {
    if (ids.length === 0) {
      return;
    }

    mediaMove(siteId, env, { ids, ...(folder === null ? {} : { folder_id: folder }) })
      .then((response) => {
        if (response.status === 200) {
          const target =
            folder === null
              ? 'в корень'
              : `в «${folderList.find((f) => f.id === folder)?.name ?? ''}»`;

          toast.success(
            `${pluralize(response.data.moved, ['файл', 'файла', 'файлов'])} — ${target}`,
          );
        }

        setSelected([]);
        refresh();
      })
      .catch(fail);
  };

  const createFolder = (parent: string | null) => {
    setNaming({
      title: 'Новая папка',
      initial: '',
      save: (name) => {
        mediaFolderCreate(siteId, env, { name, ...(parent === null ? {} : { parent_id: parent }) })
          .then((response) => {
            refresh();

            if (response.status === 201) {
              go({ view: 'folder', folder: response.data.id });
            }
          })
          .catch(fail);
      },
    });
  };

  const renameFolder = (folder: FolderBody) => {
    setNaming({
      title: 'Переименовать папку',
      initial: folder.name,
      save: (name) => {
        mediaFolderUpdate(folder.id, { name }, { 'If-Match': folder.etag })
          .then(refresh)
          .catch(fail);
      },
    });
  };

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const total = list.data?.total ?? 0;
  const detailAsset = items.find((item) => item.id === detail);
  const trash = place.view === 'trash';
  const selectable = pick === undefined && (canWrite || canRemove);

  /**
   * Выбор как в Finder и Проводнике: щелчок с Cmd/Ctrl — добавить или
   * убрать файл, с Shift — всё от прошлого щелчка до этого. Когда что-то
   * уже выбрано, обычный щелчок тоже выбирает, а не открывает карточку.
   */
  const rangeTo = (id: string): string[] | undefined => {
    const ids = items.map((item) => item.id);
    const from = anchor.current === undefined ? -1 : ids.indexOf(anchor.current);
    const to = ids.indexOf(id);

    return from === -1 || to === -1
      ? undefined
      : ids.slice(Math.min(from, to), Math.max(from, to) + 1);
  };

  const choose = (item: AssetBody, range: boolean) => {
    const span = range ? rangeTo(item.id) : undefined;

    if (many) {
      setPicked((prev) => {
        if (span !== undefined) {
          const have = new Set(prev.map((asset) => asset.id));

          return [...prev, ...items.filter((it) => span.includes(it.id) && !have.has(it.id))];
        }

        return prev.some((asset) => asset.id === item.id)
          ? prev.filter((asset) => asset.id !== item.id)
          : [...prev, item];
      });
    } else {
      setSelected((prev) => {
        if (span !== undefined) {
          return [...new Set([...prev, ...span])];
        }

        return prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id];
      });
    }

    // Shift-диапазон опору не двигает: следующий Shift-щелчок — снова от неё.
    if (span === undefined) {
      anchor.current = item.id;
    }
  };

  // Выбрать все — не одну страницу, а весь вид: папку, поиск, корзину.
  const selectAll = () => {
    if (total <= items.length) {
      setSelected(items.map((item) => item.id));

      return;
    }

    const { page: _page, per_page: _perPage, ...filters } = params;

    mediaIDs(siteId, env, filters)
      .then((response) => {
        if (response.status !== 200) return;

        setSelected(response.data.ids ?? []);

        if (response.data.truncated) {
          toast.info('Выбраны первые 5000 файлов', {
            description: 'Больше разом не выбирается — сузьте поиск или папку.',
          });
        }
      })
      .catch(fail);
  };

  // Для слушателя клавиш: он заведён один раз, а выбирать надо по текущему виду.
  const selectAllRef = useRef(selectAll);

  useEffect(() => {
    selectAllRef.current = selectAll;
  });

  const clickTile = (item: AssetBody, event: MouseEvent) => {
    const range = event.shiftKey;
    const toggle = event.metaKey || event.ctrlKey;

    if (many || (selectable && (range || toggle || selected.length > 0))) {
      choose(item, range);

      return;
    }

    if (pick !== undefined) {
      pick.onChoose(item);

      return;
    }

    anchor.current = item.id;
    setDetail(item.id);
  };

  // Cmd/Ctrl+A — выбрать все файлы на странице, Escape — снять выбор.
  // Не в полях ввода и не в окнах поверх медиатеки.
  useEffect(() => {
    if (!selectable) return;

    const keys = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      if (target.closest('input, textarea, [contenteditable="true"], [role="dialog"]') !== null) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        selectAllRef.current();
      } else if (event.key === 'Escape') {
        setSelected([]);
      }
    };

    document.addEventListener('keydown', keys);

    return () => {
      document.removeEventListener('keydown', keys);
    };
  }, [selectable, items]);

  // Хлебные крошки: путь от корня к открытой папке.
  const crumbs: FolderBody[] = [];
  let cursor = folderList.find((item) => item.id === currentFolder);

  while (cursor !== undefined) {
    crumbs.unshift(cursor);
    cursor = folderList.find((item) => item.id === cursor?.parent_id);
  }

  const heading =
    debouncedSearch !== ''
      ? `Поиск: «${debouncedSearch}» — по всей медиатеке`
      : place.view === 'folder'
        ? null
        : (QUICK.find((item) => JSON.stringify(item.place) === JSON.stringify(place))?.label ?? '');

  const dropFiles = {
    onDragOver: (event: DragEvent) => {
      // Файлы с компьютера — сюда; файлы медиатеки бросают на папку слева.
      if (canWrite && !trash && event.dataTransfer.types.includes('Files')) {
        event.preventDefault();
        setDropping(true);
      }
    },
    onDragLeave: () => {
      setDropping(false);
    },
    onDrop: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes('Files')) {
        return;
      }

      event.preventDefault();
      setDropping(false);
      uploader.send(event.dataTransfer.files, currentFolder ?? undefined);
    },
  };

  return (
    <div
      className={`${styles['library'] ?? ''} ${pick === undefined ? '' : (styles['library--pick'] ?? '')}`}
    >
      <nav className={styles['side']} aria-label="Папки и виды медиатеки">
        <FolderTree
          folders={folderList}
          current={place.view === 'folder' ? place.folder : undefined}
          canWrite={canWrite}
          onOpen={(folder) => {
            go({ view: 'folder', folder });
          }}
          onDrop={move}
          onCreate={createFolder}
          onRename={renameFolder}
          onDelete={setRemovingFolder}
        />

        <ul className={styles['quick']}>
          {QUICK.filter((item) => pick === undefined || item.place.view !== 'trash').map((item) => (
            <li key={item.label}>
              <button
                type="button"
                className={`${styles['quick__item'] ?? ''} ${
                  JSON.stringify(item.place) === JSON.stringify(place)
                    ? (styles['quick__item--active'] ?? '')
                    : ''
                }`}
                onClick={() => {
                  go(item.place);
                }}
              >
                {item.icon}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <section
        className={`${styles['main'] ?? ''} ${dropping ? (styles['main--drop'] ?? '') : ''}`}
        {...dropFiles}
      >
        <div className={styles['crumbs']}>
          {heading === null ? (
            <>
              <button
                type="button"
                className={styles['crumb']}
                onClick={() => {
                  go({ view: 'folder', folder: null });
                }}
              >
                Медиатека
              </button>
              {crumbs.map((folder) => (
                <span key={folder.id} className={styles['crumb__part']}>
                  <ChevronRight size={13} />
                  <button
                    type="button"
                    className={styles['crumb']}
                    onClick={() => {
                      go({ view: 'folder', folder: folder.id });
                    }}
                  >
                    {folder.name}
                  </button>
                </span>
              ))}
            </>
          ) : (
            <span className={styles['crumb']}>{heading}</span>
          )}
          <span className={styles['crumbs__count']}>
            {pluralize(total, ['файл', 'файла', 'файлов'])}
          </span>
        </div>

        {many && picked.length > 0 ? (
          <div className={styles['selection']}>
            <span>Отмечено {pluralize(picked.length, ['файл', 'файла', 'файлов'])}</span>
            <Button
              size="sm"
              onClick={() => {
                pick?.onChooseMany?.(picked);
                setPicked([]);
              }}
            >
              Добавить
            </Button>
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              onClick={() => {
                setPicked([]);
              }}
            >
              Снять отметки
            </Button>
          </div>
        ) : selected.length > 0 ? (
          <div className={styles['selection']}>
            <span>Выбрано {pluralize(selected.length, ['файл', 'файла', 'файлов'])}</span>
            {selected.length < total && (
              <Button size="sm" variant="ghost" tone="neutral" onClick={selectAll}>
                Выбрать все {total}
              </Button>
            )}
            {!trash && canWrite && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    tone="neutral"
                    iconLeft={<FolderInput size={14} />}
                  >
                    В папку
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onSelect={() => {
                      move(null, selected);
                    }}
                  >
                    В корень
                  </DropdownMenuItem>
                  {folderList.map((folder) => (
                    <DropdownMenuItem
                      key={folder.id}
                      onSelect={() => {
                        move(folder.id, selected);
                      }}
                    >
                      {folder.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canRemove && (
              <Button
                size="sm"
                variant="outline"
                tone={trash ? 'neutral' : 'danger'}
                iconLeft={trash ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                onClick={() => {
                  // Одним запросом, сколько бы ни выбрали.
                  const request = trash
                    ? mediaRestoreMany(siteId, env, { ids: selected })
                    : mediaTrashMany(siteId, env, { ids: selected });

                  request
                    .then((response) => {
                      const count = response.status === 200 ? response.data.affected : 0;

                      toast.success(
                        trash
                          ? `Возвращено: ${pluralize(count, ['файл', 'файла', 'файлов'])}`
                          : `В корзине: ${pluralize(count, ['файл', 'файла', 'файлов'])} — вернуть можно оттуда`,
                      );
                      setSelected([]);
                      refresh();
                    })
                    .catch(fail);
                }}
              >
                {trash ? 'Вернуть' : 'В корзину'}
              </Button>
            )}
            {canRemove && trash && (
              <Button
                size="sm"
                variant="outline"
                tone="danger"
                iconLeft={<Trash2 size={14} />}
                onClick={() => {
                  askPurge(
                    { ids: selected },
                    `Удалить навсегда ${pluralize(selected.length, ['файл', 'файла', 'файлов'])}?`,
                    'Файлы сотрутся с диска, вернуть их будет нельзя. Записи, где они стояли, останутся без них.',
                  );
                }}
              >
                Удалить навсегда
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              onClick={() => {
                setSelected([]);
              }}
            >
              Снять выбор
            </Button>
          </div>
        ) : (
          <div className={styles['toolbar']}>
            <Input
              className={styles['toolbar__search']}
              placeholder="Поиск по всей медиатеке"
              aria-label="Поиск по медиатеке"
              prefix={<Search size={15} />}
              clearable
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            {canRemove && trash && (items.length > 0 || (trashFolders.data ?? []).length > 0) && (
              <Button
                variant="outline"
                tone="danger"
                iconLeft={<Trash2 size={15} />}
                onClick={() => {
                  askPurge(
                    { all: true },
                    'Очистить корзину?',
                    `Всё в корзине — ${pluralize(total, ['файл', 'файла', 'файлов'])}${
                      (trashFolders.data ?? []).length > 0
                        ? ` и ${pluralize((trashFolders.data ?? []).length, ['папка', 'папки', 'папок'])}`
                        : ''
                    } — сотрётся навсегда. Вернуть будет нельзя.`,
                  );
                }}
              >
                Очистить корзину
              </Button>
            )}
            {canWrite && !trash && (
              <>
                <Button
                  variant="outline"
                  tone="neutral"
                  iconLeft={<FolderPlus size={15} />}
                  onClick={() => {
                    createFolder(currentFolder ?? null);
                  }}
                >
                  Папка
                </Button>
                <Button
                  iconLeft={<Upload size={15} />}
                  loading={uploader.busy}
                  onClick={() => {
                    fileInput.current?.click();
                  }}
                >
                  Загрузить
                </Button>
              </>
            )}
          </div>
        )}

        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          {...(pick?.kind === 'image' ? { accept: 'image/*' } : {})}
          onChange={(event) => {
            uploader.send(event.target.files, currentFolder ?? undefined);
            // Сброс — иначе повторный выбор того же файла не даст события.
            event.target.value = '';
          }}
        />

        {uploader.tasks.length > 0 && (
          <ul className={styles['uploads']}>
            {uploader.tasks.map((task) => (
              <li key={task.id} className={styles['upload']}>
                <span className={styles['upload__name']}>{task.name}</span>
                <span className={styles['upload__state']}>
                  {task.status === 'failed'
                    ? task.error
                    : task.status === 'done'
                      ? 'загружен'
                      : task.status === 'queued'
                        ? 'в очереди'
                        : task.size > 20 * 1024 * 1024
                          ? `${String(Math.floor((task.sent / task.size) * 100))} %`
                          : 'загружаем…'}
                </span>
                {task.status === 'uploading' && (
                  <span className={styles['upload__bar']}>
                    <span style={{ width: `${String((task.sent / task.size) * 100)}%` }} />
                  </span>
                )}
              </li>
            ))}
            {!uploader.busy && (
              <li>
                <button type="button" className={styles['link']} onClick={uploader.clear}>
                  Скрыть
                </button>
              </li>
            )}
          </ul>
        )}

        {trash && (
          <p className={styles['trash-hint']}>
            Что пролежало в корзине 30 дней, удаляется навсегда само. Вернуть до этого можно в любой
            момент.
          </p>
        )}

        {trash && (trashFolders.data ?? []).length > 0 && (
          <ul className={styles['trash-folders']}>
            {(trashFolders.data ?? []).map((folder) => (
              <li key={folder.id}>
                <span>Папка «{folder.name}»</span>
                {canRemove && (
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    iconLeft={<RotateCcw size={14} />}
                    onClick={() => {
                      mediaFolderRestore(folder.id)
                        .then(() => {
                          toast.success(`«${folder.name}» возвращена вместе с содержимым`);
                          refresh();
                        })
                        .catch(fail);
                    }}
                  >
                    Вернуть
                  </Button>
                )}
                {canRemove && (
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="danger"
                    iconLeft={<Trash2 size={14} />}
                    onClick={() => {
                      askPurge(
                        { folder_ids: [folder.id] },
                        `Удалить папку «${folder.name}» навсегда?`,
                        'Вместе с файлами, которые ушли в корзину вместе с ней. Вернуть будет нельзя.',
                      );
                    }}
                  >
                    Удалить навсегда
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {list.isLoading ? (
          <p className={styles['empty']}>Загружаем…</p>
        ) : items.length === 0 ? (
          <div className={styles['empty']}>
            <Layers size={24} />
            <p>
              {trash
                ? 'Корзина пуста.'
                : debouncedSearch !== ''
                  ? 'Ничего не нашлось.'
                  : canWrite
                    ? 'Здесь пусто. Перетащите файлы сюда или нажмите «Загрузить».'
                    : 'Здесь пусто.'}
            </p>
          </div>
        ) : (
          <ul className={styles['grid']}>
            {items.map((item) => (
              <Tile
                key={item.id}
                item={item}
                selected={
                  many ? picked.some((asset) => asset.id === item.id) : selected.includes(item.id)
                }
                chosen={pick?.chosenID === item.id}
                active={detail === item.id}
                selectable={selectable}
                draggable={pick === undefined && canWrite && !trash}
                dragIDs={selected.includes(item.id) ? selected : [item.id]}
                onSelect={(range) => {
                  choose(item, range);
                }}
                onClick={(event) => {
                  clickTile(item, event);
                }}
              />
            ))}
          </ul>
        )}

        {total > 48 && (
          <div className={styles['pages']}>
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              disabled={page === 1}
              onClick={() => {
                setPage((value) => value - 1);
              }}
            >
              Назад
            </Button>
            <span>
              {page} / {Math.ceil(total / 48)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              disabled={page * 48 >= total}
              onClick={() => {
                setPage((value) => value + 1);
              }}
            >
              Дальше
            </Button>
          </div>
        )}
      </section>

      {pick === undefined && detailAsset !== undefined && (
        <AssetDetails
          key={detailAsset.id}
          asset={detailAsset}
          siteId={siteId}
          env={env}
          canWrite={canWrite}
          canRemove={canRemove}
          onClose={() => {
            setDetail(undefined);
          }}
          onChanged={refresh}
          onPurge={() => {
            askPurge(
              { ids: [detailAsset.id] },
              `Удалить «${detailAsset.name}» навсегда?`,
              'Файл сотрётся с диска, вернуть его будет нельзя.',
            );
          }}
          pickPoster={(done) => {
            setPosterFor(() => done);
          }}
        />
      )}

      <ConfirmDialog
        open={confirm !== undefined}
        onOpenChange={(open) => {
          if (!open) setConfirm(undefined);
        }}
        title={confirm?.title ?? ''}
        description={confirm?.description ?? ''}
        confirmLabel="Удалить навсегда"
        tone="danger"
        onConfirm={async () => {
          await confirm?.run();
        }}
      />

      <NameDialog
        open={naming !== undefined}
        title={naming?.title ?? ''}
        initial={naming?.initial ?? ''}
        onOpenChange={(open) => {
          if (!open) {
            setNaming(undefined);
          }
        }}
        onSave={(name) => {
          naming?.save(name);
        }}
      />

      <RemoveFolderDialog
        folder={removingFolder}
        onClose={() => {
          setRemovingFolder(undefined);
        }}
        onRemoved={(folder) => {
          refresh();

          if (currentFolder === folder.id) {
            go({ view: 'folder', folder: null });
          }
        }}
      />

      <Dialog
        open={posterFor !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setPosterFor(undefined);
          }
        }}
      >
        {posterFor !== undefined && (
          <DialogContent title="Постер видео" size="xl">
            <MediaLibrary
              siteId={siteId}
              env={env}
              canWrite={canWrite}
              canRemove={false}
              pick={{
                kind: 'image',
                onChoose: (poster) => {
                  posterFor(poster);
                  setPosterFor(undefined);
                },
              }}
            />
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

/** Плитка файла: превью, имя, вес; флажок выбора; тащится на папку. */
function Tile({
  item,
  selected,
  chosen,
  active,
  selectable,
  draggable,
  dragIDs,
  onSelect,
  onClick,
}: {
  item: AssetBody;
  selected: boolean;
  chosen: boolean;
  active: boolean;
  selectable: boolean;
  draggable: boolean;
  dragIDs: string[];
  /** Флажок: range — щелчок с Shift, выбрать диапазон. */
  onSelect: (range: boolean) => void;
  onClick: (event: MouseEvent) => void;
}) {
  const Icon = item.kind === 'video' ? Film : item.kind === 'audio' ? Music : FileText;

  return (
    <li
      className={`${styles['tile'] ?? ''} ${selected || chosen || active ? (styles['tile--on'] ?? '') : ''}`}
      draggable={draggable}
      onDragStart={(event) => {
        event.dataTransfer.setData(MEDIA_DRAG, JSON.stringify(dragIDs));
        event.dataTransfer.effectAllowed = 'move';
      }}
    >
      {selectable && (
        <span className={styles['tile__check']}>
          <Checkbox
            size="sm"
            aria-label={`Выбрать «${item.name}»`}
            checked={selected}
            onMouseDown={(event) => {
              // Shift-щелчок иначе выделил бы текст имён между плитками.
              if (event.shiftKey) event.preventDefault();
            }}
            onClick={(event) => {
              event.preventDefault();
              onSelect(event.shiftKey);
            }}
          />
        </span>
      )}

      <button
        type="button"
        className={styles['tile__open']}
        onMouseDown={(event) => {
          if (event.shiftKey) event.preventDefault();
        }}
        onClick={onClick}
      >
        <span className={styles['tile__preview']}>
          {item.thumb_url == null ? (
            <Icon size={28} />
          ) : (
            <AuthImage
              src={item.thumb_url}
              alt={item.alt === '' ? item.name : item.alt}
              className={styles['tile__image']}
            />
          )}
        </span>
        <span className={styles['tile__name']} title={item.name}>
          {item.name}
        </span>
        <span className={styles['tile__meta']}>
          {item.width != null && item.height != null
            ? `${String(item.width)}×${String(item.height)} · `
            : ''}
          {humanSize(item.size)}
        </span>
      </button>
    </li>
  );
}

/** Удаление папки — с тем, что уйдёт вместе с ней. */
function RemoveFolderDialog({
  folder,
  onClose,
  onRemoved,
}: {
  folder: FolderBody | undefined;
  onClose: () => void;
  onRemoved: (folder: FolderBody) => void;
}) {
  const contents = useQuery({
    queryKey: ['folder-contents', folder?.id],
    enabled: folder !== undefined,
    queryFn: async () => {
      const response = await mediaFolderContents(folder?.id ?? '');

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос содержимого');
      }

      return response.data;
    },
  });

  const description = (() => {
    if (contents.data === undefined) {
      return 'Считаем, что внутри…';
    }

    const { folders, files } = contents.data;

    if (folders === 0 && files === 0) {
      return 'Папка пустая. Вернуть можно из корзины.';
    }

    const parts = [
      files > 0 ? pluralize(files, ['файл', 'файла', 'файлов']) : '',
      folders > 0
        ? pluralize(folders, ['вложенная папка', 'вложенные папки', 'вложенных папок'])
        : '',
    ].filter((part) => part !== '');

    return `Вместе с ней в корзину уйдут ${parts.join(' и ')}. Вернуть можно из корзины — всё вместе.`;
  })();

  return (
    <ConfirmDialog
      open={folder !== undefined}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title={`Убрать папку «${folder?.name ?? ''}» в корзину?`}
      description={description}
      confirmLabel="В корзину"
      tone="danger"
      onConfirm={async () => {
        if (folder === undefined) {
          return;
        }

        await mediaFolderDelete(folder.id);
        toast.success(`«${folder.name}» в корзине`);
        onRemoved(folder);
      }}
    />
  );
}
